import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Container, LoadingOverlay, Text} from '@mantine/core';
import {notifications} from '@mantine/notifications';
import {useTranslation} from 'react-i18next';
import {RouteUtils} from '@bobex/react-core/routes/RouteUtils';
import {AccountService} from '@bobex/react-core/services/AccountService';

/**
 * SSORedirect - Intermediate page that redirects authenticated users to external SSO applications
 *
 * Supports multiple SSO applications via the 'app' query parameter:
 * - ?app=bobex-sso -> Redirects to Bobex SSO
 * - ?app=domain-manager -> Redirects to Domain Manager
 *
 * Flow:
 * 1. Check if refresh_token exists
 * 2. If exists, validate it by attempting to refresh access_token
 * 3. If refresh succeeds -> redirect to SSO app with valid token
 * 4. If refresh fails (expired/invalid) -> clear tokens and redirect to login
 * 5. If no token at all -> redirect to login
 */
export default function SSORedirect() {
    const {t} = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isValidating, setIsValidating] = useState(true);

    const ssoApps: Record<string, { url: string; name: string }> = {
        'bobex-sso': {
            url: `${RouteUtils.getSSOAppUrl()}sso`,
            name: 'Bobex SSO',
        },
        'domain-manager': {
            url: `${RouteUtils.getDomainManagerAppUrl()}sso`,
            name: 'Domain Manager',
        },
        'mcp-dashboard': {
            url: `${RouteUtils.getMcpDashboardAppUrl()}sso`,
            name: 'MCP Dashboard',
        },
    };

    const app = searchParams.get('app') || 'bobex-sso'; // Default to bobex-sso

    const appConfig = ssoApps[app];


    const validateAndRedirect = async () => {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
            // No token at all - redirect to login
            navigate(`/login?redirect=sso&app=${encodeURIComponent(app)}`);
            return;
        }

        // Attempt to refresh the access_token to validate the refresh_token
        await AccountService.refreshToken().then((() => {

            const validToken = localStorage.getItem('refresh_token');
            window.location.replace(`${appConfig.url}?token=${encodeURIComponent(validToken!)}`);
        })).catch(error => {

            // Token is invalid/expired - clear storage and redirect to login
            console.error('Token validation failed:', error);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem(`user-${RouteUtils.getContext()}`);
            navigate(`/login?redirect=sso&app=${encodeURIComponent(app)}`);
            setIsValidating(false);
        })


    };


    useEffect(() => {

        if (!appConfig) {
            notifications.show({
                title: t('Error'),
                message: t('sso_redirect.invalid_app'),
                color: 'red',
            });
            navigate('/dashboard');
            return;
        }


        validateAndRedirect();
    }, [searchParams, navigate, t]);

    return (
            <Container size={420} my={40}>
                <LoadingOverlay visible={isValidating} zIndex={1000} overlayProps={{radius: 'sm', blur: 2}}/>
                <Text ta="center" size="lg" mt="xl">
                    {t('sso_redirect.redirecting')}
                </Text>
            </Container>
    );
}
